/**
 * HelpModal.js — the V3 guides and the shortcut sheet, in one place.
 *
 * The guide TEXT still lives on the backend (it is documentation, not app
 * state), but it no longer arrives as a framed second website: GuideReader
 * fetches it, keeps only the article, and renders it in this app's theme and
 * language, following guide-to-guide links without ever leaving the modal.
 *
 * Props:
 * - onClose: () => void
 */

import { useI18n } from './i18n';
import { HELP_URL } from './api';
import { macChords } from './platform';
import GuideReader from './GuideReader';

/** The fast-path, grouped so the chips stay scannable. Touch FIRST: on a phone
 *  or tablet it is not a fallback — it is the whole interface, and every verb
 *  below the keyboard groups is reachable without remembering a chord. */
const SHORTCUT_GROUPS = [
  { title: 'help.scgTouch', items: [
    ['Tap', 'help.tTap'],
    ['Double-tap', 'help.scTap'],
    ['Tap a key / the bar', 'help.tLane'],
    ['Hold', 'help.tHold'],
    ['Tap again', 'help.tMenu'],
    // The step-counting fact about the card. It is not on any entry's label, and a
    // coach who does not know it closes and re-opens the card for every instant.
    ['The card stays open', 'help.tStay'],
    ['Hold a ball', 'help.tBall'],
    ['Hold a zone', 'help.tZone'],
    ['Drag onto a player', 'help.tFollow'],
    ['Drag a group onto a player', 'help.tGroupMark'],
    ['Drag an item onto a player', 'help.tRide'],
    ['Drag the pitch handles', 'help.tHandles'],
    ['Drag or hold the ruler', 'help.tRuler'],
    ['Hold a key', 'help.tKeyDel'],
    ['Drag a key', 'help.tKeyMove'],
    ['The ↩ button', 'help.tUndo'],
    ['Hold a track', 'help.tKeyAdd'],
    ['Hold the name', 'help.tRename'],
    ['Hold empty pitch', 'help.tPitch'],
    ['The branch tree', 'help.tBranch'],
    // The scenario bar's hold. It used to be listed under Double-tap, which is not the
    // gesture — and every other hold (a key, a track, the name, empty pitch) has its
    // own row. This row names the hold ONLY: the bar's tap and double-tap doors stay
    // with the rows that own those gestures, so the fact lives in exactly one place.
    ['Hold the master bar', 'help.tMasterHold'],
    ['Drag ruler edge', 'help.tEdge'],
    ['Tap the × chip', 'help.tSpeed'],
    ['Hold armed pitch', 'help.tPlace'],
    // The label used to be the GLYPH, and the glyph is shared: ⧉ Next in line,
    // ⧉ Duplicate, ⧉ Select all and the new ⧉ Select a group all wear it, while this
    // sentence describes only the row-walking verb. A row may describe only what its
    // own label names — so the label now names that verb, and the group door gets its
    // own row below.
    ['⧉ Next in line', 'help.tLineRepeat'],
    // The step-counting fact about GROUPING, and no entry's label says it: the card can
    // gather a whole line or a whole side, and every group verb then acts on all of them
    // at once. Without this row a coach long-presses each member on the pitch — four
    // gestures for what is one tap.
    ['⧉ Select a group', 'help.tGroup'],
    // The strip's AUTO switch. The sheet already MENTIONED auto-keying — help.tTap says
    // "(AUTO records the key)" — but never said there is a switch, or where it is. On a
    // phone the only way to move things without leaving keys behind is this button; the
    // keyboard has the bare A key, and the touch path should not pay for a fact the
    // keyboard path gets for free.
    ['AUTO ON/OFF', 'help.tAuto'],
    // The front door of the app, and the touch sheet never mentioned it. help.tPlace has
    // always said "hold the ARMED pitch" — but arming happens in the toolbar's item +
    // menu, and no row said so; on a phone there is no other way to get a cone onto the
    // pitch. The label names the two triggers the row describes (+ and −), not the whole
    // toolbar: the file menu is not an animation verb, so it stays out of this row.
    ['The + and − buttons', 'help.tToolbar'],
  ] },
  { title: 'help.scgPlayback', items: [
    ['Space', 'help.scSpace'],
    ['⇧ Space', 'help.scPlayTo'],
    ['⌥ Space', 'help.scBranchPlay'],
    ['+ / −', 'help.scSpeed'],
    ['0', 'help.scSpeedReset'],
    ['Alt + / −', 'help.scStretch'],
    ['⌥ ⇧ + / −', 'help.scPadBranch'],
    ['[ / ]', 'help.scRipple'],
    ['1 2 3', 'help.scCam'],
    ['F', 'help.scFocus'],
    ['N / ⇧N', 'help.scCycle'],
    ['V', 'help.scView'],
    ['⇧ V', 'help.scHideBall'],
    ['H', 'help.scHide'],
    ['A', 'help.scAuto'],
    ['⌥ A / ⇧⌥ A', 'help.scAttach'],
    ['P', 'help.scShot'],
    ['⇧ P', 'help.scRec'],
  ] },
  { title: 'help.scgPlacement', items: [
    ['⇧ 1-6', 'help.scPlace'],
    ['D', 'help.scLineRepeat'],
    ['⌥ D', 'help.scTurnPlace'],
    ['⇧ D', 'help.scMirrorPlace'],
    ['⌥ ⇧ D', 'help.scMirrorPlaceZ'],
    ['⇧ 7 8', 'help.scPlacePlayer'],
    ['⇧ 9', 'help.scReArm'],
    ['Shift+Click', 'help.scGroup'],
  ] },
  { title: 'help.scgTiming', items: [
    ['← →', 'help.scArrows'],
    [', .', 'help.scJump'],
    ['Alt + ← →', 'help.scNudge'],
    ['S', 'help.scLandSame'],
    ['Ctrl ↑ ↓', 'help.scHeight'],
  ] },
  { title: 'help.scgItems', items: [
    ['K', 'help.scKey'],
    ['Enter', 'help.scEnterEdit'],
    ['X', 'help.scAction'],
    ['⇧ X', 'help.scRepeatAction'],
    ['C', 'help.scSeg'],
    ['M', 'help.scMirror'],
    ['⇧ M', 'help.scMirrorEnd'],
    ['⌥ M', 'help.scMirrorAll'],
    ['⇧ ⌥ M', 'help.scMirrorAllEnd'],
    ['⇧ ⌥ S', 'help.scSelectKind'],
    ['Ctrl [ ]', 'help.scBendAmount'],
    ['⇧ C', 'help.scLoft'],
    ['⌥ [ ]', 'help.scLoftDial'],
    ['⌃ ⌥ [ ]', 'help.scPeakDial'],
    ['● orange', 'help.scBend'],
    ['● violet', 'help.scArch'],
    ['G', 'help.scArrow'],
    ['⇧ G', 'help.scArrowColor'],
    ['T', 'help.scZone'],
    ['Ctrl ← →', 'help.scRadius'],
    ['⌃ arrows + grab', 'help.scVertexNudge'],
    ['⇧ A', 'help.scVertexAdd'],
    ['⇧ T', 'help.scZoneDel'],
  ] },
  { title: 'help.scgBranches', items: [
    ['B', 'help.scBranch'],
    ['⇧ R', 'help.scRename'],
    ['⇧ K', 'help.scClip'],
    ['⌥ B', 'help.scSibling'],
    ['⌥ E', 'help.scEnter'],
    ['⌥ ⌫', 'help.scDelBranch'],
    ['⇧ B', 'help.scDupBranch'],
    ['Alt 1-9', 'help.scPick'],
    ['⇧ [ ]', 'help.scSibs'],
    ['⇧ ↑ ↓', 'help.scWalk'],
    ['Alt ⇧ ↑ ↓', 'help.scPriority'],
    ['Ctrl ⇧ C / V', 'help.scBranchCopy'],
    ['⌥ ↑', 'help.scParent'],
  ] },
  { title: 'help.scgEditing', items: [
    ['Ctrl C / V', 'help.scCopy'],
    ['Ctrl D', 'help.scDup'],
    ['Del', 'help.scDelete'],
    ['⇧ Del', 'help.scDelGroup'],
    ['Ctrl Z / Y', 'help.scUndo'],
    ['Ctrl S / O', 'help.scSaveOpen'],
    ['Ctrl N', 'help.scNew'],
    ['Esc', 'help.scEsc'],
    ['2× tap', 'help.scTap'],
    ['?', 'help.scHelp'],
  ] },
];

export default function HelpModal({ onClose }) {
  const { t } = useI18n();
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998, background: 'rgba(0,0,0,0.7)',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#1e293b', borderRadius: 12, padding: '1rem 1.2rem',
        width: '100%', maxWidth: 920, height: 'calc(var(--kv-viewport-h) * 0.88)',
        display: 'flex', flexDirection: 'column', color: '#e2e8f0',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{t('help.title')}</h2>
          <button onClick={onClose} style={{
            background: '#475569', border: 'none', borderRadius: 6, color: '#fff',
            width: 30, height: 30, cursor: 'pointer',
          }}>✕</button>
        </div>
        {/* The LOOP in one line — chips teach verbs, this teaches the sentence. */}
        <div style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0 0 8px' }}>
          {t('help.coreLoop')}
        </div>
        {/* ── Keyboard fast-path chips, grouped ── */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: 10,
          padding: '0.5rem 0.6rem', background: '#0f172a', border: '1px solid #334155', borderRadius: 8,
        }}>
          {SHORTCUT_GROUPS.map(group => (
            <div key={group.title} style={{ display: 'contents' }}>
              <span style={{
                alignSelf: 'center', fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#64748b', marginRight: 2,
              }}>{t(group.title)}</span>
              {group.items.map(([keys, key]) => (
                <span key={key} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: '0.72rem', color: '#94a3b8',
                }}>
                  <kbd style={{
                    background: '#1e293b', border: '1px solid #475569', borderRadius: 4,
                    padding: '1px 5px', color: '#e2e8f0', font: '600 0.68rem ui-monospace, monospace',
                  }}>{keys}</kbd>
                  {macChords(t(key))}
                </span>
              ))}
            </div>
          ))}
        </div>
        {/* ── The guides, IN this app: fetched, stripped to the article, and
             rendered in the app's theme and language. Links between guides are
             followed here too — reading a guide never leaves the workspace. ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 6px' }}>
          <span style={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>
            {t('help.guides')}
          </span>
          <a
            href={HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
            title={t('help.guideOpenSiteHint')}
            style={{ fontSize: '0.7rem', color: '#94a3b8', textDecoration: 'none' }}
          >{t('help.guideOpenSite')}</a>
        </div>
        <GuideReader />
      </div>
    </div>
  );
}
